import { useState } from "react";
import UserLayout from "../../components/user/dashboard/UserLayout";
import { mockUserProfile } from "../../utils/mockUserData";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  ShieldCheck,
  Save,
  Camera,
  ChevronRight,
  Eye,
  EyeOff,
  X,
  Loader2
} from "lucide-react";
import { useComingSoon } from "../../utils/useComingSoon";
import { useUserAuth } from "../../services/user/UserAuthServices";

export default function AccountSettings() {
  const [profile, setProfile] = useState(mockUserProfile);
  const [loading, setLoading] = useState(false);
  const { showComingSoon } = useComingSoon();
  const { changePassword } = useUserAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const handleUpdate = (e) => {
    e.preventDefault();
    setLoading(true);
    // Mock update
    setTimeout(() => {
      setLoading(false);
      alert("Settings updated successfully!");
    }, 1000);
  };

  return (
    <UserLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1400px] mx-auto space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-medium text-[#111111] dark:text-white font-serif">Account Settings</h1>
          <p className="text-[#666666] dark:text-[#A0A0A0] mt-1">Manage your profile and security preferences.</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Info Form */}
          <div className="xl:col-span-2 space-y-6">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm p-8 md:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-900/10 flex items-center justify-center">
                  <User size={20} className="text-[#62397f]" />
                </div>
                <h2 className="text-xl font-medium text-[#111111] dark:text-white font-serif">Personal Profile</h2>
              </div>

              <form onSubmit={handleUpdate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] ml-1">Full Name</label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                      <input
                        type="text"
                        value={profile.fullName}
                        onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                        className="w-full pl-12 pr-4 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] ml-1">Email Address</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        className="w-full pl-12 pr-4 py-3.5 bg-[#F3F4F6] dark:bg-[#151515] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm text-[#999999] cursor-not-allowed font-inter"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] ml-1">Phone Number</label>
                    <div className="relative group">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="w-full pl-12 pr-4 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-[#666666] dark:text-[#A0A0A0] ml-1">Home Address</label>
                    <div className="relative group">
                      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                      <input
                        type="text"
                        value={profile.address}
                        onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                        className="w-full pl-12 pr-4 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 px-8 py-3.5 bg-[#62397f] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-95 transition-all text-sm disabled:opacity-50"
                  >
                    <Save size={18} />
                    {loading ? "Saving Changes..." : "Save Settings"}
                  </button>
                </div>
              </form>
            </div>

            {/* Security Section */}
            <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm p-8 md:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/10 flex items-center justify-center">
                  <ShieldCheck size={20} className="text-red-500" />
                </div>
                <h2 className="text-xl font-medium text-[#111111] dark:text-white font-serif">Security & Privacy</h2>
              </div>

              <div className="space-y-4">
                <button
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="w-full flex items-center justify-between p-4 rounded-2xl border border-[#F0F0F0] dark:border-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#212121] transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-gray-100 dark:bg-[#0A0A0A] flex items-center justify-center group-hover:bg-[#62397f] group-hover:text-white transition-colors">
                      <Lock size={18} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-[#111111] dark:text-white font-serif">Change Password</p>
                      <p className="text-xs text-[#666666] dark:text-[#A0A0A0]">Update your account security regularly</p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-[#999999]" />
                </button>

                <button
                  onClick={() => showComingSoon({
                    title: "Two-Factor Authentication Coming Soon!",
                    message: "We're building the 2FA feature. Stay tuned!"
                  })}
                  className="w-full flex items-center justify-between p-4 rounded-2xl border border-[#F0F0F0] dark:border-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#212121] transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-gray-100 dark:bg-[#0A0A0A] flex items-center justify-center group-hover:bg-[#62397f] group-hover:text-white transition-colors">
                      <ShieldCheck size={18} />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-[#111111] dark:text-white font-serif">Two-Factor Authentication</p>
                      <p className="text-xs text-[#666666] dark:text-[#A0A0A0]">Add an extra layer of security</p>
                    </div>
                  </div>
                  <div className="px-3 py-1 bg-gray-100 dark:bg-[#2A2A2A] rounded-lg text-[10px] font-bold text-[#666666] dark:text-[#A0A0A0] uppercase">Not Enabled</div>
                </button>
              </div>
            </div>
          </div>

          {/* Profile Sidebar Info */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm p-8 text-center space-y-6">
              <div className="relative w-32 h-32 mx-auto group">
                <div className="w-full h-full bg-gradient-to-br from-[#62397f] to-[#7C3AED] rounded-full flex items-center justify-center shadow-xl">
                  <User size={64} className="text-white" />
                </div>
                <button title="Change Photo" className="absolute bottom-1 right-1 w-10 h-10 bg-white dark:bg-[#262626] rounded-full shadow-lg border border-[#F0F0F0] dark:border-[#333333] flex items-center justify-center text-[#62397f] hover:scale-110 active:scale-95 transition-all">
                  <Camera size={20} />
                </button>
              </div>
              <div>
                <h3 className="text-xl font-medium text-[#111111] dark:text-white font-serif">{profile.fullName}</h3>
                <p className="text-sm text-[#666666] dark:text-[#A0A0A0]">Joined {profile.joinDate}</p>
              </div>

              <div className="pt-6 grid grid-cols-2 gap-4 border-t border-[#F0F0F0] dark:border-[#2A2A2A]">
                <div>
                  <p className="text-xs text-[#999999] mb-1">Total Rentals</p>
                  <p className="text-lg font-medium text-[#111111] dark:text-white font-serif">12</p>
                </div>
                <div>
                  <p className="text-xs text-[#999999] mb-1">Tier Level</p>
                  <p className="text-lg font-medium text-[#62397f] font-serif">Silver</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1E1E1E] to-[#111111] rounded-3xl p-8 text-white shadow-xl space-y-4">
              <h4 className="font-medium font-serif">Need Help?</h4>
              <p className="text-sm text-gray-400 leading-relaxed">If you have issues with your profile or account data, contact our administrative support team.</p>
              <button
                onClick={() => showComingSoon({
                  title: "Support Coming Soon!",
                  message: "We're building the support ticket feature. Stay tuned!"
                })}
                className="w-full py-3 bg-[#62397f] text-white font-bold rounded-2xl hover:bg-[#7C3AED] transition-all text-sm"
              >
                Open Support Ticket
              </button>
            </div>
          </div>
        </div>
      </div>
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={changePassword}
      />
      <style jsx="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </UserLayout>
  );
}

function ChangePasswordModal({ isOpen, onClose, onConfirm }) {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await onConfirm(formData);
    if (res.status === "success") {
      setFormData({ current_password: "", new_password: "", confirm_password: "" });
      onClose();
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-6 border-b border-[#F0F0F0] dark:border-[#2A2A2A] flex items-center justify-between">
          <h3 className="text-xl font-medium text-[#111111] dark:text-white font-serif text-center flex-1">Change Password</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] rounded-xl transition-colors">
            <X size={20} className="text-[#999999]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider ml-1">Current Password</label>
            <div className="relative group">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
              <input
                type={showCurrent ? "text" : "password"}
                required
                value={formData.current_password}
                onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#62397f]"
              >
                {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider ml-1">New Password</label>
            <div className="relative group">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
              <input
                type={showNew ? "text" : "password"}
                required
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#62397f]"
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-[#666666] dark:text-[#A0A0A0] uppercase tracking-wider ml-1">Confirm New Password</label>
            <div className="relative group">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
              <input
                type={showConfirm ? "text" : "password"}
                required
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="w-full pl-12 pr-12 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 transition-all font-inter"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999999] hover:text-[#62397f]"
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="pt-4 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#666666] dark:text-[#A0A0A0] rounded-2xl text-sm font-bold hover:bg-gray-50 dark:hover:bg-[#212121] transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3.5 bg-[#62397f] text-white rounded-2xl text-sm font-bold shadow-lg shadow-purple-200 dark:shadow-none hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : "Update Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
